import React, { useCallback, useEffect, useState } from "react";
import useContract from "../../hooks/useContract";
import { Page, Panel, formatRaw, formatDate, getError, USD_DECIMALS } from "./ktlShared";

const History = () => {
  const { address, isConnected, getCurrentLeagueId, getLeague, getLeagueUserStats, getClaimHistory, getRewardTokenData } = useContract();
  const [rows, setRows] = useState([]);
  const [token, setToken] = useState({ symbol: "KIND", decimals: 18 });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!address) return;
    try {
      const [currentId, reward] = await Promise.all([getCurrentLeagueId(), getRewardTokenData()]);
      const requests = [];
      for (let id = 1n; id <= currentId; id += 1n) {
        requests.push(Promise.all([getLeague(id), getLeagueUserStats(id, address), getClaimHistory(id, address)])
          .then(([league, stats, claims]) => ({ id, league, stats, claims: Array.from(claims || []) })));
      }
      setRows((await Promise.all(requests)).reverse());
      setToken(reward);
    } catch (e) { setError(getError(e)); }
  }, [address, getClaimHistory, getCurrentLeagueId, getLeague, getLeagueUserStats, getRewardTokenData]);

  useEffect(() => { load(); }, [load]);

  if (!isConnected) return <Page eyebrow="League archive" title="History"><Panel>Connect your wallet.</Panel></Page>;

  return (
    <Page eyebrow="League archive" title="History" description="Previous participation, KP, volume, rewards and claims.">
      {error && <div className="mb-5 rounded-xl bg-red-400/10 p-4 text-red-300">{error}</div>}
      <div className="space-y-4">
        {rows.length ? rows.map((row) => {
          const claim = row.claims[row.claims.length - 1];
          return (
            <Panel key={row.id.toString()}>
              <div className="flex justify-between gap-4">
                <div><p className="text-xs text-[#ffcc66]">League #{row.id.toString()}</p><h3 className="mt-1 text-xl font-black">{row.league?.finalized ? "Finalized" : row.league?.active ? "Active" : "Inactive"}</h3><p className="mt-1 text-xs text-[#777]">{formatDate(row.league?.startAt)} — {formatDate(row.league?.endAt)}</p></div>
                <span className="text-xs text-[#999]">{row.stats?.joined ? "Participated" : "No Participation"}</span>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl bg-black/20 p-4"><p className="text-xs text-[#777]">KP</p><p className="mt-2 text-lg font-black">{row.stats?.kp?.toString() || "0"} KP</p></div>
                <div className="rounded-xl bg-black/20 p-4"><p className="text-xs text-[#777]">Volume</p><p className="mt-2 text-lg font-black">${formatRaw(row.stats?.volumeUsd, USD_DECIMALS)}</p></div>
                <div className="rounded-xl bg-black/20 p-4"><p className="text-xs text-[#777]">Reward</p><p className="mt-2 text-lg font-black text-[#ffcc66]">{formatRaw(claim?.allocated || row.stats?.claimableReward, token.decimals, 4)} {token.symbol}</p></div>
                <div className="rounded-xl bg-black/20 p-4"><p className="text-xs text-[#777]">Claim</p><p className="mt-2 text-lg font-black">{claim?.claimedStatus ? "Claimed" : row.stats?.claimableReward > 0n ? "Available" : "None"}</p></div>
              </div>
              {claim && <p className="mt-4 border-t border-white/[0.06] pt-4 text-xs text-[#777]">Claimed {formatRaw(claim.claimed, token.decimals, 4)} {token.symbol} · {formatDate(claim.claimedAt)}</p>}
            </Panel>
          );
        }) : <Panel><p className="text-center text-[#666]">No history found.</p></Panel>}
      </div>
    </Page>
  );
};

export default History;
